import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import md5 from 'md5';

import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { AccountConfiguration } from '../../../dashboard/models/account-configuration.model';
import { AccountConfigurationService } from '../../../dashboard/services/account-configuration.service';
import { PromotionCouponsService } from '../../../promotion-coupons/services/promotion-coupons.service';
import { AuthStoreService } from '../../../shared/services/auth/auth-store.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { AnalyticsEvent, AnalyticsService } from '../../services/analytics-service';
import { AppConfig } from '../../services/app-config.service';
import { HandsetService } from '../../services/handset.service';
import { SavedUrlService } from '../../services/saved-url.service';
import { ToolbarDefinition, ToolbarService } from '../../services/toolbar.service';
import { LiabilityService } from '../../../liabilities/services/liability.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportDataDialogComponent } from '../export-data-dialog/export-data-dialog.component';
import { MainNotificationService } from '../../services/main-notification.service';
import { AccountConsentsService } from '../../../account-consents/components/account-consents.service';
import { CustomerStoreService } from '../../services/customer-store.service';
import { CrispService } from '../../services/crisp.service';
import { Crisp } from 'crisp-sdk-web';
import { CustomerService } from '../../services/customer.service';
import { UrlStoreService } from '../../../account/services/url-store.service';
import { ContentDataService } from '../../../liabilities/services/content-data.service';

@Component({
standalone: false,
  selector: 'app-toolbar',
  styleUrls: ['./toolbar.component.scss'],
  templateUrl: './toolbar.component.html',
})
export class ToolbarComponent implements OnInit, AfterViewInit {
  @Input() public scrolled = false;
  @Output() public mainNotificationHeightChange: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('mainNotification') mainNotification: ElementRef;

  public searchToggleVisible = false;
  public searchVisible = false;
  public search = new FormControl();
  public photoUrl = null;
  public emailMD5: string;
  public toolbarDefinition: ToolbarDefinition;
  public configurationMessage$: Observable<AccountConfiguration>;
  public tagsSelectedIds: string[] = [];
  public isImpersonalization = false;
  private mainNotificationHeight: number;

  constructor(
    public promotionCouponsService: PromotionCouponsService,
    public toolbarService: ToolbarService,
    public authService: AuthStoreService,
    public handsetService: HandsetService,
    public loadingService: LoadingService,
    private router: Router,
    public appConfig: AppConfig,
    private elementRef: ElementRef,
    private accountConfigurationService: AccountConfigurationService,
    private analyticsService: AnalyticsService,
    private savedUrlService: SavedUrlService,
    private route: ActivatedRoute,
    private liabilityService: LiabilityService,
    private dialog: MatDialog,
    private mainNotificationService: MainNotificationService,
    public accountConsentsService: AccountConsentsService,
    public customerStoreService: CustomerStoreService,
    public customerService: CustomerService,
    private crispService: CrispService,
    private contentStoreService: ContentDataService,
    private urlStore: UrlStoreService,
  ) {
    this.handsetService.xsChanges.subscribe((isXs) => {
      if (isXs && !this.search.value) {
        this.closeSearch();
      }
    });

    this.configurationMessage$ = this.accountConfigurationService.accountConfiguration$;
    this.toolbarService.resize$.subscribe(() => this.resizeToolbar());
  }

  get height(): any {
    return this.elementRef.nativeElement.offsetHeight;
  }

  public ngOnInit(): void {
    this.emailMD5 = md5(this.authService.userInfo.email) as string;
    this.toolbarService.toolbarDefinition.subscribe(
      (toolbarDefinition) => (this.toolbarDefinition = toolbarDefinition),
    );
    this.search.valueChanges.subscribe(this.toolbarService.search);
    this.toolbarService.search
      .pipe(
        filter((query) => this.search.value !== query),
        tap((query) => this.search.setValue(query)),
      )
      .subscribe();
    const { queryParamMap } = this.route.snapshot;
    this.tagsSelectedIds.push(queryParamMap.get('tag'));
    this.isImpersonalization = this.authService.isImpersonalization();
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.onWindowResize();
    }, 500);
  }

  @HostListener('window:resize', ['$event'])
  public onWindowResize(): void {
    this.resizeToolbar();
  }

  private resizeToolbar() {
    if (
      window.innerWidth > 600 ||
      (!this.appConfig.config.endOfServiceDate &&
        !this.appConfig?.config?.applicationReadOnlyMode &&
        !this.appConfig?.config?.migration?.bannerEnabled &&
        this.appConfig?.config?.payment?.autoPayment?.enabled)
    ) {
      this.mainNotificationService.setMainNotificationHeight(null);
    } else if (this.mainNotificationHeight !== this.mainNotification?.nativeElement?.offsetHeight) {
      this.mainNotificationService.setMainNotificationHeight(
        this.mainNotification?.nativeElement?.offsetHeight ? this.mainNotification.nativeElement.offsetHeight : null,
      );
      this.mainNotificationHeight = this.mainNotification?.nativeElement?.offsetHeight;
    }
  }

  public clearSearch(): void {
    this.search.setValue(null);
    if (this.handsetService.isXs) {
      this.closeSearch();
    }
  }

  public openIntercom(): void {
    if (this.appConfig.config.analytics.intercom.enabled) {
      Intercom('showNewMessage');
    }
  }

  public openCrisp(): void {
    if (window['$crisp']) {
      Crisp.chat.open();
    } else {
      console.error('Czat z konsultantem niedostępny');
    }
  }

  public logout(): void {
    this.authService.logout()
      .pipe(
        tap(() => this.contentStoreService.clear()),
      )
      .subscribe(() => {
        this.savedUrlService.clear();
      });

    this.crispService.resetCrispSession();
  }

  public showSearch(): void {
    this.searchToggleVisible = false;
    this.searchVisible = true;
  }

  private closeSearch(): void {
    this.searchToggleVisible = true;
    this.searchVisible = false;
  }

  public configurationToolbarClicked(): void {
    this.analyticsService.trackEvent(new AnalyticsEvent('CHECKLIST_TOOLBAR'));
  }

  public tagsSelected(tagIds: string[]): void {
    this.toolbarService.tagIds$.next(tagIds);
    this.liabilityService.tagIds$.next(tagIds);
  }

  public onFetchCustomerDataClick(): void {
    this.dialog.open(ExportDataDialogComponent);
  }

  public onMigrationConsentsClick(): void {
    this.router.navigate(['/consents']);
  }

  public verifyAccountClick() {
    if (this.router.url !== '/account/verification') {
      this.urlStore.setState(this.router.url);
    }
    this.router.navigateByUrl('/account/verification');
  }

  public revertImpersonalization(){
    this.authService.revertImpersonation()
  }

  public isImpersionalized(){
    this.authService.isImpersonalization();
  }

}
